import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, MoreHorizontal, Pencil, Trash2, AlertCircle, Package,
  ToggleLeft, ToggleRight, PackagePlus, PackageMinus,
} from "lucide-react";

const TABS = [
  { id: "uniforms", label: "Uniforms" },
  { id: "books", label: "Books" },
  { id: "consumables", label: "Consumables" },
  { id: "others", label: "Others" },
];

export default function SuppliesPage() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms.inventory_create;
  const canEdit = isOwner || !!perms.inventory_edit;
  const canDelete = isOwner || !!perms.inventory_delete;
  const canAddStock = isOwner || !!perms.inventory_add_stock;
  const canDeductStock = isOwner || !!perms.inventory_deduct_stock;

  const [tab, setTab] = useState("uniforms");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [stockDialog, setStockDialog] = useState(null); // { item, mode: 'add' | 'deduct' }

  const branchId = selectedBranch?.id;

  const load = useCallback(async () => {
    if (!branchId) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/inventory-items", { params: { branch_id: branchId } });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((it) => it.category === tab), [items, tab]);
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const toggleStatus = async (it) => {
    const next = it.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/inventory-items/${it.id}`, { status: next });
      toast.success(`Item ${next === "active" ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!selectedBranch) {
    return (
      <>
        <Header />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Supplies &amp; inventories are scoped per branch.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        {/* Tabs */}
        <div className="inline-flex p-1 rounded-2xl bg-brand-purpleLight/50 border border-brand-purple/10" role="tablist" data-testid="supplies-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id} role="tab" type="button"
                onClick={() => setTab(t.id)}
                data-testid={`supplies-tab-${t.id}`}
                className={`px-4 sm:px-5 h-9 rounded-xl text-sm font-heading font-semibold transition ${
                  active ? "bg-white shadow-sm text-brand-purple" : "text-brand-inkMuted hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {canCreate && (
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5"
            data-testid="add-supply-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm" data-testid="supplies-table-wrap">
        {loading ? (
          <p className="p-6 text-brand-inkMuted">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="supplies-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
              <Package className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No {TABS.find((t) => t.id === tab).label.toLowerCase()} yet</p>
            <p className="text-brand-inkMuted mt-1 text-sm">Click Add Item to record your first item.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="supplies-table">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th className="text-left px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">ITEM NAME</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">OPENING STOCKS</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">CLOSING STOCKS</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">UNIT PRICE</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">STATUS</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, i) => (
                  <tr key={it.id} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`supply-row-${it.id}`}>
                    <td className="px-5 py-3.5 font-heading font-semibold text-foreground whitespace-nowrap uppercase">{it.name}</td>
                    <td className="px-5 py-3.5 text-center tabular-nums text-foreground whitespace-nowrap">{it.opening_stocks ?? 0}</td>
                    <td className="px-5 py-3.5 text-center tabular-nums text-foreground whitespace-nowrap">{it.closing_stocks ?? 0}</td>
                    <td className="px-5 py-3.5 text-center tabular-nums text-foreground whitespace-nowrap">{fmt(it.unit_price)}</td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      <StatusPill status={it.status} />
                    </td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`supply-actions-${it.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                          {canAddStock && (
                            <DropdownMenuItem onClick={() => setStockDialog({ item: it, mode: "add" })} data-testid={`supply-add-stock-${it.id}`}>
                              <PackagePlus className="w-4 h-4 mr-2" /> Add Stock
                            </DropdownMenuItem>
                          )}
                          {canDeductStock && (
                            <DropdownMenuItem onClick={() => setStockDialog({ item: it, mode: "deduct" })} data-testid={`supply-deduct-stock-${it.id}`}>
                              <PackageMinus className="w-4 h-4 mr-2" /> Deduct Stock
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={() => { setEditing(it); setOpen(true); }} data-testid={`supply-edit-${it.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={() => toggleStatus(it)} data-testid={`supply-toggle-${it.id}`}>
                              {it.status === "active" ? <ToggleLeft className="w-4 h-4 mr-2" /> : <ToggleRight className="w-4 h-4 mr-2" />}
                              {it.status === "active" ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem onClick={() => setDeleting(it)} className="text-[#C62828]" data-testid={`supply-delete-${it.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <ItemFormDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          branchId={branchId}
          defaultCategory={tab}
          editing={editing}
          currency={currency}
          onSaved={(savedCat) => {
            setOpen(false); setEditing(null);
            if (savedCat && savedCat !== tab) setTab(savedCat);
            load();
          }}
        />
      )}

      <AddStockDialog
        state={stockDialog}
        currency={currency}
        onClose={() => setStockDialog(null)}
        onSaved={() => { setStockDialog(null); load(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this item?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              <span className="font-heading text-foreground">{deleting?.name}</span> will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/inventory-items/${deleting.id}`);
                  toast.success("Item deleted");
                  setDeleting(null);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-supply-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Header() {
  return (
    <div className="mb-5">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Supplies &amp; Inventories</h1>
      <p className="text-brand-inkMuted mt-0.5 text-sm">Track uniforms, books, consumables and other branch supplies.</p>
    </div>
  );
}

function ItemFormDialog({ open, onClose, branchId, defaultCategory, editing, currency, onSaved }) {
  const init = editing || {};
  const [category, setCategory] = useState(init.category || defaultCategory || "uniforms");
  const [name, setName] = useState(init.name || "");
  const [opening, setOpening] = useState(init.opening_stocks != null ? String(init.opening_stocks) : "0");
  const [closing, setClosing] = useState(init.closing_stocks != null ? String(init.closing_stocks) : "");
  const [unitPrice, setUnitPrice] = useState(init.unit_price != null ? String(init.unit_price) : "0");
  const [status, setStatus] = useState(init.status || "active");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Item name is required"); return; }
    const payload = {
      category,
      name: name.trim(),
      opening_stocks: Number(opening || 0),
      closing_stocks: closing === "" ? Number(opening || 0) : Number(closing),
      unit_price: Number(unitPrice || 0),
      status,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/inventory-items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory-items", { branch_id: branchId, ...payload });
        toast.success("Item added");
      }
      onSaved(category);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="supply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit item" : "Add item"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Record a supply or inventory item for this branch.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Category <span className="text-[#C62828]">*</span></Label>
            <select
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="supply-category-select"
            >
              {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="supply-name" className="font-heading">Item Name <span className="text-[#C62828]">*</span></Label>
            <Input
              id="supply-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. T-SHIRT (S10)" className="rounded-2xl h-11 mt-1.5 uppercase"
              data-testid="supply-name-input" autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="supply-opening" className="font-heading">Opening Stocks</Label>
              <Input
                id="supply-opening" type="number" min="0" value={opening}
                onChange={(e) => setOpening(e.target.value)}
                className="rounded-2xl h-11 mt-1.5 tabular-nums"
                data-testid="supply-opening-input"
              />
            </div>
            <div>
              <Label htmlFor="supply-closing" className="font-heading">Closing Stocks</Label>
              <Input
                id="supply-closing" type="number" min="0" value={closing}
                onChange={(e) => setClosing(e.target.value)}
                placeholder={`Defaults to opening (${opening || 0})`}
                className="rounded-2xl h-11 mt-1.5 tabular-nums"
                data-testid="supply-closing-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="supply-price" className="font-heading">Unit Price</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted">{currency}</span>
              <Input
                id="supply-price" type="number" min="0" step="0.01" value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                className="rounded-2xl h-11 pl-8 tabular-nums"
                data-testid="supply-price-input"
              />
            </div>
          </div>
          <div>
            <Label className="font-heading">Status</Label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="supply-status-select"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="supply-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="supply-submit-btn">
              {saving ? "Saving..." : (editing ? "Save" : "Add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[12px] font-heading font-semibold text-white ${
        active ? "bg-brand-purple" : "bg-brand-inkMuted"
      }`}
      data-testid={`status-${status}`}
    >
      {active ? "active" : "inactive"}
    </span>
  );
}


function AddStockDialog({ state, currency, onClose, onSaved }) {
  const item = state?.item || null;
  const mode = state?.mode === "deduct" ? "deduct" : "add";
  const isDeduct = mode === "deduct";

  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) { setQty(""); setNote(""); }
  }, [item?.id, mode]);

  if (!item) return null;

  const current = Number(item.closing_stocks || 0);
  const parsedQty = parseInt(qty, 10);
  const validPositive = Number.isFinite(parsedQty) && parsedQty > 0;
  const overDraft = isDeduct && validPositive && parsedQty > current;
  const valid = validPositive && !overDraft;
  const projected = valid
    ? (isDeduct ? current - parsedQty : current + parsedQty)
    : current;

  const cfg = isDeduct
    ? {
        title: "Deduct Stock",
        verb: "Deduct",
        replenishOrIssue: "Issue, transfer, or write-off",
        addColor: "text-[#C2410C]",
        addSign: "−",
        newColor: "text-[#B45309]",
        endpoint: `/inventory-items/${item.id}/deduct-stock`,
        placeholder: "e.g. Issued 12 units to Grade 1 classroom",
        toastVerb: "Deducted",
        btnLabel: "Deduct Stock",
        btnClass: "bg-[#C2410C] hover:bg-[#9A3412] text-white",
        savingLabel: "Deducting...",
      }
    : {
        title: "Add Stock",
        verb: "Replenish",
        replenishOrIssue: "The quantity you enter is added to the current closing stocks",
        addColor: "text-brand-purple",
        addSign: "+",
        newColor: "text-emerald-700",
        endpoint: `/inventory-items/${item.id}/add-stock`,
        placeholder: "e.g. Bought from XYZ Supplier - OR #12345",
        toastVerb: "Added",
        btnLabel: "Add Stock",
        btnClass: "bg-brand-purple hover:bg-brand-purpleHover text-white",
        savingLabel: "Adding...",
      };

  async function submit(e) {
    e.preventDefault();
    if (!validPositive) { toast.error("Enter a positive quantity"); return; }
    if (overDraft) { toast.error(`Only ${current} in stock — cannot deduct ${parsedQty}`); return; }
    setSaving(true);
    try {
      const { data } = await api.post(cfg.endpoint, {
        quantity: parsedQty,
        note: note.trim(),
      });
      const changed = data.added ?? data.deducted ?? parsedQty;
      toast.success(`${cfg.toastVerb} ${changed} unit(s) — ${item.name}`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={isDeduct ? "deduct-stock-dialog" : "add-stock-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">{cfg.title}</DialogTitle>
          <DialogDescription>
            {cfg.verb} <span className="font-semibold text-foreground uppercase">{item.name}</span>. {cfg.replenishOrIssue}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid={isDeduct ? "deduct-stock-form" : "add-stock-form"}>
          <div className="grid grid-cols-3 gap-2 text-center bg-brand-purpleLight/40 rounded-2xl p-3">
            <div>
              <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Current</div>
              <div className="font-heading text-2xl font-bold text-foreground tabular-nums">{current}</div>
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">{cfg.addSign} {isDeduct ? "Deduct" : "Add"}</div>
              <div className={`font-heading text-2xl font-bold ${cfg.addColor} tabular-nums`}>
                {validPositive ? `${cfg.addSign}${parsedQty}` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">= New</div>
              <div className={`font-heading text-2xl font-bold ${cfg.newColor} tabular-nums`} data-testid={isDeduct ? "deduct-stock-projected" : "add-stock-projected"}>
                {projected}
              </div>
            </div>
          </div>

          {overDraft && (
            <div className="rounded-2xl bg-[#FEE2E2] border border-red-200 px-3 py-2 text-xs text-[#991B1B] flex items-center gap-2" data-testid="stock-overdraft-warning">
              <AlertCircle className="w-3.5 h-3.5" />
              Only {current} in stock — you cannot deduct more than that.
            </div>
          )}

          <div>
            <Label htmlFor="stock-qty">Quantity to {isDeduct ? "deduct" : "add"} <span className="text-red-500">*</span></Label>
            <Input
              id="stock-qty"
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="e.g. 24"
              autoFocus
              data-testid={isDeduct ? "deduct-stock-qty-input" : "add-stock-qty-input"}
            />
          </div>

          <div>
            <Label htmlFor="stock-note">Note (optional)</Label>
            <Input
              id="stock-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              placeholder={cfg.placeholder}
              data-testid={isDeduct ? "deduct-stock-note-input" : "add-stock-note-input"}
            />
          </div>

          <div className="text-xs text-brand-inkMuted">
            Unit price stays at <span className="font-semibold text-foreground">{currency}{Number(item.unit_price || 0).toLocaleString(undefined,{ minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>. Use Edit if you need to change it.
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl" data-testid={isDeduct ? "deduct-stock-cancel" : "add-stock-cancel"}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !valid}
              className={`rounded-2xl ${cfg.btnClass}`}
              data-testid={isDeduct ? "deduct-stock-save" : "add-stock-save"}
            >
              {saving ? cfg.savingLabel : cfg.btnLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
